import { useMemo } from 'react'
import { useLayerDataFetcher } from '../../../data_fetchers/layerDataFetcher'
import { useLayers } from '@app/contexts/LayerContext'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { useDispatch, useSelector } from 'react-redux'
import { updateMapStateProperty } from '@app/redux/prioritisationMap/prioritisationMapSlice'
import { addSelectedMarkerToMap } from '@app/components/Molecules/MapView/GenericMapView/GenericMapView.utilities'
import {
  generateVulnerabilityPalette,
  getLayersByType,
} from '@app/components/Molecules/MapView/RiskMapView/RiskMapView.utilities'
import { toast } from '@app/components/ui/Toast/toast'
import apiClient from '@app/config/axios.config'
import { theme } from '@app/styles/theme'
import * as Sentry from '@sentry/react'
import { RootState } from '@app/redux/store'
import { gridPaginatedVisibleSortedGridRowIdsSelector, useGridApiRef } from '@mui/x-data-grid'
import { getClientAssetId, getElementName } from './RiskTableUtils'

export interface AssetLayerDetails {
  name: string
  tileSetId: string
}
interface RiskTableRow {
  id: number
  assetId: string
  clientAssetId: string | number
  assetType: string
  assetLayerDetails: AssetLayerDetails
  elementName: string
  hazardAssetTag: string
  hazardDisplayName: string
  consequence: number
  exposure_value: string | number
  cost: string
  action: string
  vulnerabilityStyle: number
  unit?: string
}

export const useRiskTable = () => {
  const layerData = useLayerDataFetcher()
  const { vulnerabilityData } = useLayers()
  const gridApiRef = useGridApiRef()
  const assetLayerDetails = useMemo(() => {
    return layerData
      ? Object.entries(layerData.assetDialogData)
          .flatMap(([, category]) => category.groups.flatMap((group) => group.assets))
          .reduce(
            (acc, asset) => {
              acc[asset.type] = {
                name: asset.display_name,
                tileSetId: asset.tilesets[0].id,
              }
              return acc
            },
            {} as Record<string, AssetLayerDetails>,
          )
      : {}
  }, [layerData])

  const hazardDetails = useMemo(() => {
    const toReturn: Record<string, { displayName: string; unit?: string }> = {}
    layerData?.hazardDialogData.hazardDetails.forEach((hazardDetail) =>
      hazardDetail.scenarios.forEach((scenario) => {
        toReturn[scenario.assetTag] = {
          displayName: scenario.display_name,
          unit: scenario.tilesets[0].unit,
        }
      }),
    )
    return toReturn
  }, [layerData])

  const riskTableRows: RiskTableRow[] = useMemo(() => {
    const uniqueAssets: { [assetId: string]: RiskTableRow } = {}
    let id = 0

    Object.entries(vulnerabilityData).forEach(([assetType, assetHazardInstances]) => {
      Object.entries(assetHazardInstances).forEach(
        ([hazardAssetTag, assetHazardInstancesByAssetId]) => {
          Object.entries(assetHazardInstancesByAssetId).forEach(
            ([assetId, assetHazardInstance]) => {
              const uniqueAssetKey = `${assetType}|${assetId}`
              const current = uniqueAssets[uniqueAssetKey]
              if (current) {
                if (current.vulnerabilityStyle < assetHazardInstance.vulnerability_style) {
                  current.vulnerabilityStyle = assetHazardInstance.vulnerability_style
                  current.hazardDisplayName = hazardDetails[hazardAssetTag].displayName
                  current.hazardAssetTag = hazardAssetTag
                  current.exposure_value = assetHazardInstance.exposure_value
                  current.unit = hazardDetails[hazardAssetTag].unit
                  current.consequence = assetHazardInstance.vulnerability_style
                }
              } else {
                uniqueAssets[uniqueAssetKey] = {
                  id: id++,
                  assetId,
                  clientAssetId: '...',
                  assetLayerDetails: assetLayerDetails[assetType],
                  assetType,
                  elementName: '...',
                  hazardDisplayName: hazardDetails[hazardAssetTag].displayName,
                  hazardAssetTag,
                  consequence: assetHazardInstance.vulnerability_style,
                  exposure_value: assetHazardInstance.exposure_value,
                  unit: hazardDetails[hazardAssetTag].unit,
                  cost: '...',
                  action: 'Zoom to',
                  vulnerabilityStyle: assetHazardInstance.vulnerability_style,
                }
              }
            },
          )
        },
      )
    })

    return Object.values(uniqueAssets)
  }, [vulnerabilityData, assetLayerDetails, hazardDetails])

  const { layers, legendsData } = useSelector((state: RootState) => state.riskMap)
  const { flyTo, map, selectFeature, deselectFeature } = useMap()
  const dispatch = useDispatch()
  const zoomTo = async (
    assetType: string,
    assetLayerDetails: AssetLayerDetails,
    assetId: string,
  ) => {
    try {
      deselectFeature()
      const result = await apiClient.get(`/asset/location/${assetType}/${assetId}`)
      const [lng, lat] = result.data
      flyTo({
        latitude: lat,
        longitude: lng,
        zoom: 18, // TODO: Use a padded bounding box of feature's geojson
        usePadding: true,
        duration: 2000,
      })

      map?.once('moveend', (_e) => {
        const selectedFeature = map.queryRenderedFeatures({
          layers: [assetLayerDetails.tileSetId ?? ''],
          filter: ['==', ['get', 'asset_id'], Number.parseInt(assetId)],
        })[0]
        if (!selectedFeature) {
          // This happens if the feature is too small (a pipe section for example) for the
          // map to render at end zoom level of the fly to. No easy fix and doesn't cause
          // significant problems.
          return
        }

        const assetLayers = getLayersByType(layers, 'asset')
        const selectedAssetLayer = assetLayers.find(
          (assetLayer) => assetLayer.tilesets[0].id === assetLayerDetails.tileSetId,
        )
        if (!selectedAssetLayer) {
          Sentry.captureException(
            `Couldn't find asset layer '${assetLayerDetails.tileSetId}' on moveend in RiskTable.`,
          )
          return
        }

        selectFeature(selectedFeature, selectedAssetLayer, 'risk_table')
        addSelectedMarkerToMap({
          map,
          coordinates: { lat, lng },
          colour: theme.palette.primary.main,
        })

        dispatch(
          updateMapStateProperty({
            selectedMarker: {
              longitude: lng,
              latitude: lat,
            },
          }),
        )
      })
    } catch (_) {
      toast({ description: 'Location not available', type: 'warning' })
    }
  }

  const handleOnDataGridPageRender = async () => {
    // Had severe problems with this seemingly not firing when columns were sorted, turns
    // out it *was* firing but looking like it was not updating the datagrid. This is because
    // visibleRows was out of date so the rows it was updating weren't visible.
    //
    // Fun.
    //
    // Got Claude on the case (with A LOT of guidance from me), this is its solution:
    //
    //    sortModel is kept in React state (rather than using DataGrid's initialState) so that
    //    onSortModelChange can update it as controlled state. This is necessary because
    //    onSortModelChange fires synchronously within MUI's internal state update cycle, before
    //    the grid has finished recomputing the sorted row order. By storing the sort model in
    //    React state and reacting to it via useEffect, we guarantee that gridPaginatedVisibleSortedGridRowIdsSelector
    //    is only called after the DataGrid has processed the new sort model and committed to the DOM.
    //
    if (!gridApiRef.current) return
    const visibleRows = gridPaginatedVisibleSortedGridRowIdsSelector(gridApiRef)
    if (visibleRows.length === 0) {
      return
    }
    const uniqueAssetTypeIds = visibleRows.map((visibleRowId) => {
      const row = gridApiRef.current?.getRow(visibleRowId)
      return `${row['assetType']}|${row['assetId']}`
    })

    try {
      // Because assets are uniquely identified by a combination of assetType and assetId
      // we need to send a list like so:
      // e687_l0_v10|9188663,e687_l0_v10|9188686,e687_l0_v10|9188693
      // Shouldn't have more than a couple of dozen values in the list as it only requests details
      // for the on screen rows
      const response = await apiClient.get(
        `/asset/identifying_information?assetTypeIds=${uniqueAssetTypeIds.join(',')}`,
      )
      visibleRows.forEach((rowRef) => {
        const oldRow = gridApiRef.current?.getRow(rowRef)
        if (!oldRow) return // Happens when sidedraw is closed

        const identifyingInformation = response.data[oldRow['assetType']][oldRow['assetId']]

        // The community version of the Data Grid is limited to a single row update per apiRef.current.updateRows()
        gridApiRef.current?.updateRows([
          {
            ...oldRow,
            cost: identifyingInformation['replacement_cost'],
            elementName: getElementName(identifyingInformation, '-'),
            clientAssetId: getClientAssetId(identifyingInformation, '-'),
          },
        ])
      })
    } catch (e) {
      toast({ description: 'Failed to update Element name and Cost columns', type: 'warning' })
      Sentry.captureException(e)
    }
  }

  const filterOptions = useMemo(() => {
    const bgColors = generateVulnerabilityPalette(legendsData, 'vulnerability')
    return {
      consequences: [
        { label: 'Not Exposed', bgColor: bgColors[0], fgColor: '#000000' },
        { label: 'Insignificant', bgColor: bgColors[1], fgColor: '#714544' },
        { label: 'Minor', bgColor: bgColors[2], fgColor: '#5C4017' },
        { label: 'Moderate', bgColor: bgColors[3], fgColor: '#FFFFFF' },
        { label: 'Major', bgColor: bgColors[4], fgColor: '#FFFFFF' },
        { label: 'Exposed', bgColor: bgColors[5], fgColor: '#201A2F' },
      ],
      elementTypes: [
        ...new Set(riskTableRows.map((r) => r.assetLayerDetails.name).filter(Boolean)),
      ].sort(),
      riskSources: [
        ...new Set(riskTableRows.map((r) => r.hazardDisplayName).filter(Boolean)),
      ].sort(),
    }
  }, [legendsData, riskTableRows])

  return { riskTableRows, zoomTo, gridApiRef, handleOnDataGridPageRender, filterOptions }
}
